import type { ReactNode } from "react";
import { AlertTriangle, Check, Loader2, Unlink } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { cn } from "@/shared/lib/utils";

import type { DomainCard } from "../../api/types";
import {
  APEX_HINT,
  domainStatusLabel,
  domainStatusTone,
  domainSteps,
  stepStates,
  type StepState,
} from "../../model/domainStatus";
import { DnsTable } from "./DnsTable";
import { ToneBadge } from "./ToneBadge";

/**
 * 屏 5「自有域名 · {domain}」卡:状态徽标 + 解绑 + 四步(填写域名 → 验证所有权 → 配置解析 → 证书签发 + 上线)。
 *
 * ★ 四步点亮全从 `overallStatus` 派生(`stepStates`);`error` 把出问题的那一步标红并贴 `lastCheckNote`。
 * ★ 第 2 / 3 步里让用户抄的主机记录、CNAME 目标都读 api(`records` / `gatewayHost`),不写死;api 没给那一行
 *   就写「见下表」,不自己猜一个 `_siteverify` / `www` / `@`(主机名相对主域算,猜错了用户照抄就配错,附录 A.37)。
 * ★ 设计稿第 3 步写「我们每 5 分钟自动检测一次」:api 这期**没有**定时检测(只有手动 check + 30s 频控),
 *   照实写「配好后点『立即检测』」,不许诺做不到的事(与内容编辑页「自动保存」同一条处理)。
 * ★ 设计稿第 1 步写「裸域会自动 301 过去」:api 这期**没有** A 记录 / 301(网关 IP 未定,规格 §9.1),裸域要解析商支持
 *   CNAME 拉平 —— 照 api 的说法写,不许诺 301。
 * ★ 模拟发布(`simulated`,附录 A.38):第 4 步不提证书 / HTTPS,上线后也不给 `https://` 链接(stub 网关什么都没发出去)。
 */
export function CustomDomainCard({
  card,
  gatewayHost,
  simulated = false,
  hostNote = null,
  canUnbind = true,
  busy,
  unbinding,
  onUnbind,
}: {
  card: DomainCard;
  gatewayHost: string;
  simulated?: boolean;
  /** host 模式宿主签发状态人话(A.54),第 4 步停在签发时显示。 */
  hostNote?: string | null;
  /** STAFF 看不到解绑(A.59)。 */
  canUnbind?: boolean;
  /** 页上有写口在跑(检测 / 解绑),解绑按钮上锁。 */
  busy: boolean;
  unbinding: boolean;
  onUnbind: () => void;
}) {
  const states = stepStates(card);
  const txtHost = card.records.find((r) => r.type === "TXT")?.host;
  const cnameHost = card.records.find((r) => r.type === "CNAME")?.host;
  const errorNote = card.overallStatus === "error" ? card.lastCheckNote : null;

  const bodies: ReactNode[] = [
    <>
      <span className="font-mono text-foreground">{card.domain}</span> · {APEX_HINT}。
    </>,
    <>
      在域名 DNS 里加一条 TXT 记录
      {txtHost ? (
        <>
          {" "}
          <Code>{txtHost}</Code>
        </>
      ) : (
        NOT_GIVEN
      )}
      ,证明域名是你的。
      {card.verificationStatus === "verified" && "已验证通过。"}
      {card.verificationStatus !== "verified" && (
        // W14(1013):用户把这一步当成「只读、点不动」——它本来就不在本页操作,要说清去哪做、做完回来点什么。
        <span data-domain-step2-howto="" className="mt-1 block text-foreground">
          这一步不在本页填写:请登录你的域名服务商(如阿里云、腾讯云、华为云)的 DNS 解析控制台,
          按下方表格把 TXT 和 CNAME
          两条记录都加上;加好后回到本页点「立即检测」,通过后这里会自动打勾。
        </span>
      )}
    </>,
    <>
      把
      {cnameHost ? (
        <>
          {" "}
          <Code>{cnameHost}</Code>{" "}
        </>
      ) : (
        NOT_GIVEN
      )}
      的 CNAME 指到 <Code>{gatewayHost}</Code>。DNS 生效通常 10 分钟到 2
      小时,配好后点「立即检测」查看进度。
    </>,
    simulated ? (
      <>
        发布服务器还没接入:解析生效后按「模拟发布」直接标为上线,不对公网提供访问。
        {card.overallStatus === "live" && " 已上线(模拟发布)。"}
      </>
    ) : (
      <>
        解析生效后自动申请 HTTPS 证书,签发成功即对外可访问,不用你操作。
        {hostNote && card.overallStatus === "issuing" && ` 当前:${hostNote}。`}
        {card.overallStatus === "live" && (
          <>
            {" "}
            已上线:
            <a
              href={`https://${card.domain}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-primary underline-offset-2 hover:underline"
            >
              https://{card.domain}
            </a>
          </>
        )}
      </>
    ),
  ];

  return (
    <section
      aria-label={`自有域名 · ${card.domain}`}
      className={cn("border border-border bg-card p-4 text-card-foreground", CARD_WALL_RADIUS)}
    >
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h2 className="truncate text-sm font-semibold text-foreground">
            自有域名 · {card.domain}
          </h2>
          <ToneBadge tone={domainStatusTone(card.overallStatus)}>
            {domainStatusLabel(card.overallStatus, simulated)}
          </ToneBadge>
          {card.icpNo && <ToneBadge tone="muted">备案号 {card.icpNo}</ToneBadge>}
        </div>
        {canUnbind && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            disabled={busy}
            onClick={onUnbind}
          >
            {unbinding ? <Loader2 className="animate-spin" /> : <Unlink />}
            解绑
          </Button>
        )}
      </header>

      <ol aria-label="绑定步骤" className="mt-4 space-y-4">
        {domainSteps(simulated).map((title, i) => (
          <li
            key={title}
            data-state={states[i]}
            aria-current={states[i] === "current" || states[i] === "error" ? "step" : undefined}
            className="flex gap-3"
          >
            <StepMark n={i + 1} state={states[i]} />
            <div className="min-w-0 flex-1 space-y-2">
              <h3
                className={cn(
                  "text-sm font-semibold",
                  states[i] === "todo" ? "text-muted-foreground" : "text-foreground"
                )}
              >
                {title}
                {states[i] === "error" && <span className="sr-only">(异常)</span>}
              </h3>
              <p className="text-xs leading-relaxed text-muted-foreground">{bodies[i]}</p>
              {states[i] === "error" && errorNote && (
                <p
                  role="note"
                  className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-foreground"
                >
                  {errorNote}
                </p>
              )}
              {i === 2 && card.records.length > 0 && <DnsTable records={card.records} />}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

/** api 没给那一行主机记录时的说法:指向下面的 DNS 表,不编一个主机名。 */
const NOT_GIVEN = "(见下表)";

function Code({ children }: { children: ReactNode }) {
  return (
    <code className="rounded bg-muted px-1 py-0.5 font-mono text-2xs text-foreground">
      {children}
    </code>
  );
}

function StepMark({ n, state }: { n: number; state: StepState }) {
  return (
    <span
      aria-hidden
      className={cn(
        "mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-semibold",
        state === "done" && "bg-success/15 text-success",
        state === "current" && "bg-primary text-primary-foreground",
        state === "error" && "bg-destructive text-destructive-foreground",
        state === "todo" && "border border-border text-muted-foreground"
      )}
    >
      {state === "done" ? (
        <Check className="h-3.5 w-3.5" />
      ) : state === "error" ? (
        <AlertTriangle className="h-3.5 w-3.5" />
      ) : (
        n
      )}
    </span>
  );
}
